import { format } from 'date-fns'
import { addSeconds } from 'date-fns'
import memoizeOne from 'memoize-one'
import pluralize from 'pluralize'
import { round } from 'remeda'

import {
  HOUR_SECONDS,
  HR_MIN_SEC_FORMAT,
  MIN_SEC_FORMAT,
  MIN_SEC_FRACTION_FORMAT,
  SEC_FORMAT,
} from '@hap/constants'

enum NumberFormat {
  Seconds = 'Seconds',
  FactorialMinutes = 'FactorialMinutes',
  MinutesSeconds = 'MinutesSeconds',
  HoursMinutesSeconds = 'HoursMinutesSeconds',
}

export const numberFormatLabelMap: Record<NumberFormat, string> = {
  [NumberFormat.Seconds]: 'seconds',
  [NumberFormat.FactorialMinutes]: 'minutes',
  [NumberFormat.MinutesSeconds]: 'mm:ss',
  [NumberFormat.HoursMinutesSeconds]: 'hh:mm:ss',
}

export const getStringFormatLabel = (str = ''): string => {
  const numberFormat = getStringFormat(str)
  return numberFormat === '' ? '' : numberFormatLabelMap[numberFormat]
}

export const getStringFormat = (str = ''): NumberFormat | '' => {
  if (SEC_FORMAT.test(str)) {
    return NumberFormat.Seconds
  } else if (MIN_SEC_FORMAT.test(str)) {
    return NumberFormat.MinutesSeconds
  } else if (MIN_SEC_FRACTION_FORMAT.test(str)) {
    return NumberFormat.FactorialMinutes
  } else if (HR_MIN_SEC_FORMAT.test(str)) {
    return NumberFormat.HoursMinutesSeconds
  }

  return ''
}

export const humanizeSecondsToMinutes = memoizeOne(
  (seconds?: number | string, suffix: string | false = 'min'): string => {
    if (typeof seconds !== 'number' && typeof seconds !== 'string') return '—'
    const minutes = round(parseInt(`${seconds}`) / 60, 2)
    const label = suffix ? pluralize(suffix, minutes) : ''
    return `${minutes}${label ? ' ' + label : ''}`
  },
)

export const secondsToTime = memoizeOne(
  (durationSecs?: number | string | null, timeFormat = 'mm:ss'): string => {
    durationSecs = Number(durationSecs)

    if (isNaN(durationSecs) || !isFinite(durationSecs)) return '—'

    const hours = Math.floor(durationSecs / HOUR_SECONDS)
    const minutes = Math.floor((durationSecs % HOUR_SECONDS) / 60)
    const seconds = Math.round(durationSecs % 60)

    if (timeFormat === 'HH:mm:ss') {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
    }

    if (durationSecs > 3599 && timeFormat === 'mm:ss') {
      return `${Math.floor(durationSecs / 60)}:${seconds < 10 ? '0' + seconds : seconds}`
    }

    const helperDate = addSeconds(new Date(0), isNaN(durationSecs) ? 0 : durationSecs)
    return `${format(helperDate, timeFormat)}`
  },
)
